<template>
  <Modal
    inner-class="rounded flex flex-col h-full pb-8 sm:pb-4"
    custom-class="w-full max-w-[768px]"
    size="none"
    :is-full-height="false"
    is-add-to-basket-popover
    @close="closeAddToBasketFlyout"
  >
    <div
      class="flex h-full flex-col"
      :class="{
        'pb-14': isSafari && isMobile,
      }"
    >
      <div class="flex shrink-0 flex-row items-center px-4 py-5 pr-12">
        <div class="flex items-center lg:bg-green-20 lg:px-1.5 lg:py-0.5">
          <IconCheckmark class="size-6 pr-2" />
          <Headline tag="p" class="text-base sm:text-2lg" :is-uppercase="false">
            {{ $t('basket.add_to_basket_info') }}
          </Headline>
        </div>
      </div>
      <div class="px-4 pb-4">
        <hr class="w-full border-t border-black-10" />
      </div>
      <div class="px-4 pb-4 sm:hidden">
        <BasketItemsNotReservedMessage />
      </div>
      <div class="flex-1 overflow-y-auto [scrollbar-gutter:stable_both-edges]">
        <ClientOnly>
          <div class="relative">
            <article
              v-if="lastItemAddedToBasket"
              :key="lastItemAddedToBasket.key"
            >
              <BasketCardPopover
                is-light-variant
                :product="lastItemAddedToBasket.product"
                :variant="lastItemAddedToBasket.variant"
                :title="
                  getBasketCardPopoverTitle(
                    lastItemAddedToBasket.product.attributes,
                  )
                "
                :size="getBasketCardPopoverSize(lastItemAddedToBasket.variant)"
                :color="
                  getProductColors(lastItemAddedToBasket.product, 'color').join(
                    '/',
                  )
                "
                :article-no="lastItemAddedToBasket.variant.referenceKey || ''"
                :image-hash="lastItemImage?.hash"
                :reduced-price="getDiscountPrice(lastItemAddedToBasket.price)"
                :price="lastItemAddedToBasket.price.total.withTax"
                :quantity="lastItemAddedToBasket.quantity"
                :available-quantity="lastItemAddedToBasket.availableQuantity"
                :show-price-reduction="isActiveSalePeriod"
                :is-sold-out="lastItemAddedToBasket.product.isSoldOut"
                :has-basket-action="false"
                :has-quantity-action="false"
                :has-wishlist-action="false"
                :is-add-to-basket-popover="isAddToBasketFlyoutOpen"
                :order-number="
                  (lastItemAddedToBasket?.customData as BasketItemCustomData)
                    ?.ordernumber
                "
                @close="closeAddToBasketFlyout"
              >
                <template v-if="isAddToBasketFlyoutOpen">
                  <BasketCTAs
                    is-add-to-basket-popover
                    is-desktop
                    class="hidden sm:flex"
                    @close="closeAddToBasketFlyout"
                  />
                </template>
              </BasketCardPopover>
            </article>

            <div
              class="hidden items-center justify-between gap-4 px-4 pb-4 sm:flex"
            >
              <BasketItemsNotReservedMessage tone="grey" />
              <button
                class="cursor-pointer text-sm text-black underline hover:no-underline"
                data-qa="continue-shopping"
                @click="closeAddToBasketFlyout"
              >
                {{ $t('basket.continue_shopping_label') }}
              </button>
            </div>

            <BasketCTAs
              class="sm:hidden"
              is-add-to-basket-popover
              @close="closeAddToBasketFlyout"
            />
          </div>
          <ProductRecommendations
            v-if="lastItemAddedToBasket?.product?.id"
            :token="OdoscopeRecommendationsToken.CART_LAYER"
            :title="$t('recommendations.title.cart_layer')"
            :product-id="lastItemAddedToBasket.product.id"
            :is-add-to-basket-popover="true"
            :center-content="false"
            @click:recommendation="handleRecommendationsClick"
          />
        </ClientOnly>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import type { Variant, Attributes } from '@scayle/storefront-nuxt'
import {
  getAppliedReductionsByCategory,
  getFirstAttributeValue,
  getProductColors,
} from '@scayle/storefront-nuxt'
import { fetchOnNavigation } from '#layers/base/utils/loading'
import useSmartShopConfiguration from '#layers/base/composables/useSmartShopConfiguration'
import useUiState from '~/composables/ui/useUiState'
import useAgsBasket from '~/composables/useAgsBasket'
import { getFrontImage } from '#layers/base/utils/image'
import type { BasketItemCustomData } from '#layers/base/types/basket'
import ProductRecommendations from '~/components/product/ProductRecommendations.vue'
import { OdoscopeRecommendationsToken } from '#layers/base/constants/recommendations'
import useBrowserDetection from '#layers/base/composables/useBrowserDetection'
import { getSizeLabel } from '#layers/base/types/variant'

const basket = useAgsBasket()
const {
  closeAddToBasketFlyout,
  isAddToBasketFlyoutOpen,
  lastVariantIdAddedToBasket,
} = useUiState()

const { isSafari } = useBrowserDetection()
const { isMobile } = useDeviceType()

const { isActiveSalePeriod } = useSmartShopConfiguration()
const lastItemAddedToBasket = basket.items.value.length
  ? (basket.items.value.find(
      (p) => p.variant.id === lastVariantIdAddedToBasket.value,
    ) ?? basket.items.value[0])
  : null
const lastItemImage = computed(() =>
  lastItemAddedToBasket
    ? getFrontImage(lastItemAddedToBasket.product.images)
    : undefined,
)

// Fetch data
await fetchOnNavigation(basket.fetch)

const getDiscountPrice = (price: any) =>
  getAppliedReductionsByCategory(price.total, 'sale')[0]?.amount.absoluteWithTax

const getBasketCardPopoverTitle = (attrs?: Attributes) =>
  getFirstAttributeValue(attrs, 'name')?.label || ''

const getBasketCardPopoverSize = (variant: Variant) =>
  getSizeLabel(variant) || ''

const handleRecommendationsClick = () => {
  closeAddToBasketFlyout()
}
</script>
